/**
 * Council Trail v0 — REST wrappers (Phase 4 spec §11.1 routes).
 *
 * Same-origin cookies attach automatically. All routes are gated by
 * verify_session_ownership on the backend; non-owner returns 403,
 * CostBudgetInsufficient → 402 envelope (D-12.5).
 */
import type {
  CouncilState,
  InvitePeersInput,
  InvitePeersOutput,
  NudgePeerInput,
  NudgePeerOutput,
  ProposalRow,
  ProposeInviteTarget,
  ProposeMergeTarget,
  ResolveProposalInput,
  ResolveProposalOutput,
  SpineReplyAck,
  ForkAck,
  WakeBriefPayload,
} from '../types/council';

async function _json<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    throw new CouncilApiError(resp.status, await _safeBody(resp));
  }
  return resp.json() as Promise<T>;
}

async function _safeBody(resp: Response): Promise<unknown> {
  try {
    return await resp.json();
  } catch {
    return { text: await resp.text().catch(() => '') };
  }
}

export class CouncilApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`council api error ${status}`);
  }
}

/** Thrown when a request aborts on the client-side timeout (vs a server reply).
 *  Distinct name so callers can retry idempotent sends without importing the
 *  class (jest module-mocks of this module would shadow it). */
export class CouncilTimeoutError extends Error {
  constructor() {
    super('council request timed out');
    this.name = 'CouncilTimeoutError';
  }
}

/** Spine-reply ack round-trip cap. The reply QUEUES server-side, so this guards
 *  only the synchronous ack — a hung fetch otherwise wedges ackInFlightRef and
 *  the composer (SP-3a follow-up). */
const SPINE_REPLY_TIMEOUT_MS = 15000;

export interface MigrateRecentTrailsEntry {
  session_id: string;
  trail_id: string;
  last_message_at: string;
  trail_name?: string;
}

export interface MigrationResult {
  migrated: number;
  skipped: number;
}

export interface CouncilMetadataPatch {
  hill_to_climb?: string | null;
}

export const councilApiClient = {
  async getCouncilState(sessionId: string): Promise<CouncilState> {
    return _json<CouncilState>(await fetch(
      `/api/sessions/${sessionId}/council`,
      { credentials: 'same-origin' },
    ));
  },

  /** v0.1.x Item 3 — upsert writable session_council_metadata fields.
   *  Returns the refreshed CouncilState so the caller can replace its cache. */
  async patchMetadata(
    sessionId: string,
    payload: CouncilMetadataPatch,
  ): Promise<CouncilState> {
    return _json<CouncilState>(await fetch(
      `/api/sessions/${sessionId}/council/metadata`,
      {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  async inviteCrew(
    sessionId: string,
    payload: InvitePeersInput,
  ): Promise<InvitePeersOutput> {
    return _json<InvitePeersOutput>(await fetch(
      `/api/sessions/${sessionId}/council/invite`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  /** C2.1 sp4 — user invite affordance: enqueue a GATED propose_invite (proposer
   *  resolved server-side to the session core persona). Materializes on approve. */
  async proposeInvite(
    sessionId: string,
    body: ProposeInviteTarget,
  ): Promise<{ id: string; kind: string; status: string }> {
    return _json<{ id: string; kind: string; status: string }>(await fetch(
      `/api/sessions/${sessionId}/council/propose-invite`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ));
  },

  /** C2.2 — user fold-back affordance: enqueue a GATED propose_merge (proposer
   *  resolved server-side to the session core persona). Materializes on approve. */
  async proposeMerge(
    sessionId: string,
    body: ProposeMergeTarget,
  ): Promise<{ id: string; kind: string; status: string }> {
    return _json<{ id: string; kind: string; status: string }>(await fetch(
      `/api/sessions/${sessionId}/council/propose-merge`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ));
  },

  /** Reply-targeting §2a — spine-addressed operator reply. Returns the ACK;
   *  the resident's answer arrives async via TURN_ADDED reload. P2: the
   *  dispatched[] values are per-resident (each at their own camp). */
  async sendSpineReply(
    sessionId: string,
    spineRootMessageId: string,
    body: { message: string; message_id?: string },
    opts?: { timeoutMs?: number },
  ): Promise<SpineReplyAck> {
    const ctrl = new AbortController();
    const timer = setTimeout(
      () => ctrl.abort(), opts?.timeoutMs ?? SPINE_REPLY_TIMEOUT_MS);
    try {
      return await _json(await fetch(
        `/api/sessions/${sessionId}/spines/${spineRootMessageId}/reply`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: ctrl.signal,
        },
      ));
    } catch (err: any) {
      // Same message_id on retry is exactly-once-safe (backend dedup) — surface
      // a distinct error so the caller knows it may retry.
      if (err?.name === 'AbortError') throw new CouncilTimeoutError();
      throw err;
    } finally {
      clearTimeout(timer);
    }
  },

  /** §2a — mid-spine fork. Branch a new spine from from_message_id (steer mints an
   *  envelope; reroll has none). Returns the ACK; the new branch's answer arrives
   *  async via TURN_ADDED reload. */
  async sendFork(
    sessionId: string,
    spineRootMessageId: string,
    body: { from_message_id: string; mode: 'steer' | 'reroll';
            fork_id: string; message?: string; message_id?: string },
  ): Promise<ForkAck> {
    return _json(await fetch(
      `/api/sessions/${sessionId}/spines/${spineRootMessageId}/fork`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ));
  },

  async nudge(
    sessionId: string,
    payload: NudgePeerInput,
  ): Promise<NudgePeerOutput> {
    return _json<NudgePeerOutput>(await fetch(
      `/api/sessions/${sessionId}/council/nudge`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  async dismiss(
    sessionId: string,
    payload: { persona_id: string },
  ): Promise<{ persona_id: string; dismissed_at: string | null; reason?: string }> {
    return _json(await fetch(
      `/api/sessions/${sessionId}/council/dismiss`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  /**
   * Backend returns 200 with `null` body when no brief is due. Errors throw.
   */
  async getWakeBrief(
    sessionId: string,
    since: string,
  ): Promise<WakeBriefPayload | null> {
    const resp = await fetch(
      `/api/sessions/${sessionId}/council/wake-brief?since=${encodeURIComponent(since)}`,
      { credentials: 'same-origin' },
    );
    return _json<WakeBriefPayload | null>(resp);
  },

  async markBriefRead(
    sessionId: string,
    briefId: string,
  ): Promise<{ id: string; read_at: string | null }> {
    return _json(await fetch(
      `/api/sessions/${sessionId}/council/wake-brief/${briefId}/read`,
      { method: 'POST', credentials: 'same-origin' },
    ));
  },

  /** Council Trail v0.1 Item 8 — list proposals for a session. `status`
   *  defaults to 'pending' on the backend, which is the common case. */
  async listProposals(
    sessionId: string,
    status: 'pending' | 'approved' | 'dismissed' = 'pending',
  ): Promise<{ proposals: ProposalRow[] }> {
    const url = `/api/sessions/${sessionId}/council/proposals?status=${status}`;
    return _json<{ proposals: ProposalRow[] }>(
      await fetch(url, { credentials: 'same-origin' }),
    );
  },

  /** Resolve a proposal — approve materializes the kind-specific side-effect
   *  (for kind='propose_fork', appends a peer message at parent_message_id +
   *  emits TURN_ADDED). Dismiss flips status only. 409 = already resolved
   *  by another tab; UI should refresh + show a "raced" toast. */
  async resolveProposal(
    sessionId: string,
    proposalId: string,
    payload: ResolveProposalInput,
  ): Promise<ResolveProposalOutput> {
    return _json<ResolveProposalOutput>(await fetch(
      `/api/sessions/${sessionId}/council/proposals/${proposalId}/resolve`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  // Cockpit S3 — set the per-session governor posture (first autonomy write client).
  async setGovernorPolicy(
    sessionId: string,
    posture: 'advisory' | 'gated' | 'autonomous',
    actionClass: string = 'throttle',
  ): Promise<{ session_id: string; action_class: string; posture: string }> {
    return _json(await fetch(
      `/api/sessions/${sessionId}/autonomy/governor-policy`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_class: actionClass, posture }),
      },
    ));
  },

  // TH-1 Living Trailhead — fire the sherpa greeting on fresh-session arrival.
  async kickoffGreeting(sessionId: string): Promise<{ session_id: string; queued: boolean }> {
    return _json(await fetch(`/api/sessions/${sessionId}/kickoff`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }));
  },

  async getRecentTrails(limit = 50): Promise<{ trails: unknown[] }> {
    return _json(await fetch(
      `/api/users/me/recent-trails?limit=${limit}`,
      { credentials: 'same-origin' },
    ));
  },

  async migrateRecentTrails(
    entries: MigrateRecentTrailsEntry[],
  ): Promise<MigrationResult> {
    return _json<MigrationResult>(await fetch(
      `/api/users/me/recent-trails/migrate`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries }),
      },
    ));
  },

  /** G3b — approve/dismiss a pending governor decision. Approve mutates the
   *  enforced store; the live GOVERNOR_APPLIED/DISMISSED frame is the row's only
   *  status writer. 409 = stale/raced; the UI re-enables the row for retry. */
  async resolveGovernorDecision(
    sessionId: string, decisionId: string,
    payload: { decision: 'approve' | 'dismiss' },
  ): Promise<{ status: string }> {
    return _json(await fetch(
      `/api/sessions/${sessionId}/governor/decisions/${decisionId}/resolve`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ));
  },

  /** G3b — backfill pending decisions so aged-out asks (no live frame) stay
   *  actionable across reload. */
  async getGovernorDecisions(
    sessionId: string, status?: string,
  ): Promise<{ decisions: any[] }> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return _json(await fetch(
      `/api/sessions/${sessionId}/governor/decisions${q}`,
      { credentials: 'same-origin' },
    ));
  },
};
